/**
 * useToasts - Toast notification management
 * 
 * Provides a simple API for showing toast notifications
 * with automatic dismissal and various severity levels.
 * 
 * API is compatible with the existing Toast component in App.tsx.
 */

import { useState, useCallback } from 'react';
import { Toast as ToastComponent } from '../components/Toast';

// Re-export the Toast type from the component for compatibility
export type { Toast } from '../components/Toast';
export type ToastType = 'success' | 'error' | 'warning' | 'info';

// Alias the component's Toast type
type Toast = ToastComponent;

export interface UseToastsResult {
    toasts: Toast[];
    /**
     * Show a toast notification
     * @param type - Toast type: 'success' | 'error' | 'warning' | 'info'
     * @param message - Message to display
     * @param duration - Optional duration in ms (default: 4000)
     */
    showToast: (type: ToastType, message: string, duration?: number) => string;
    showSuccess: (message: string, duration?: number) => string;
    showError: (message: string, duration?: number) => string;
    showWarning: (message: string, duration?: number) => string;
    showInfo: (message: string, duration?: number) => string;
    removeToast: (id: string) => void;
    clearAllToasts: () => void;
}

const DEFAULT_DURATION = 4000;

export function useToasts(): UseToastsResult {
    const [toasts, setToasts] = useState<Toast[]>([]);

    // Generate unique ID (same as App.tsx)
    const generateId = useCallback(() => {
        return Date.now().toString() + Math.random();
    }, []);

    // Add a toast with auto-dismiss
    const addToast = useCallback((type: ToastType, message: string, duration?: number): string => {
        const id = generateId();
        const toastDuration = duration || DEFAULT_DURATION;

        const newToast: Toast = {
            id,
            type,
            message,
            duration: toastDuration
        };

        setToasts(prev => [...prev, newToast]);

        // Auto-dismiss is handled by ToastItem component
        // But we also set a cleanup here as backup
        setTimeout(() => {
            removeToast(id);
        }, toastDuration + 500);

        return id;
    }, [generateId]);

    // Remove a specific toast
    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    // Clear all toasts
    const clearAllToasts = useCallback(() => {
        setToasts([]);
    }, []);

    // Main showToast function - matches App.tsx signature: (type, message, duration?)
    const showToast = useCallback((
        type: ToastType,
        message: string,
        duration?: number
    ): string => {
        return addToast(type, message, duration);
    }, [addToast]);

    // Convenience methods
    const showSuccess = useCallback((message: string, duration?: number): string => {
        return addToast('success', message, duration);
    }, [addToast]);

    const showError = useCallback((message: string, duration?: number): string => {
        return addToast('error', message, duration || 6000); // Errors stay longer
    }, [addToast]);

    const showWarning = useCallback((message: string, duration?: number): string => {
        return addToast('warning', message, duration);
    }, [addToast]);

    const showInfo = useCallback((message: string, duration?: number): string => {
        return addToast('info', message, duration);
    }, [addToast]);

    return {
        toasts,
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        removeToast,
        clearAllToasts,
    };
}

// Export the ToastContainer component for convenience
export { ToastContainer } from '../components/Toast';

